import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ErrorFlash } from "../components/ErrorFlash";
import { FormFieldLabel } from "../components/FormFieldLabel";
import {
  PermissionFormFields,
  REGISTRATION_STEPS,
  registrationChrome,
  registrationStepForField,
} from "../components/PermissionFormFields";
import { QuestionFill } from "../components/QuestionDesign";
import { PrototypeBanner, PublicNav, StaffNav } from "../components/ui";
import { remoteClient } from "../data/remote";
import { canEditStudent } from "../domain/auth";
import { runtimeFromBrowser, sampleToolsEnabled } from "../domain/env";
import {
  emptyPermission,
  permissionCore,
  permissionForEdit,
  permissionIssue,
  type PermissionAnswers,
} from "../domain/permissionForm";
import { randomAnswerForQuestion, randomPermissionAnswers } from "../domain/randomStudent";
import { missingRequiredQuestion, type FormQuestion } from "../domain/questions";
import { useAuth } from "../state/AuthProvider";
import { answersFromStudent, formCopyForLocale, useForms } from "../state/forms";
import { useLocale } from "../state/i18n";
import { useStudents } from "../state/students";

export function StudentEdit() {
  const { studentId = "" } = useParams();
  const { account, loading } = useAuth();
  const { getStudent } = useStudents();
  const { forms, submissionsForStudent } = useForms();
  const { locale, t } = useLocale();
  const student = getStudent(studentId);
  const signInTo = `/signin?next=${encodeURIComponent(`/student/${studentId}/edit`)}`;

  if (loading) return <Status note={t("edit.loading")} />;
  if (!account) return <Navigate to={signInTo} replace />;
  if (!canEditStudent(account, studentId)) {
    return <Navigate to={student ? `/student/${student.id}/profile` : "/"} replace />;
  }

  const formsReady = !remoteClient() || forms.length > 0;
  if (!student || !formsReady) {
    return <Status note={t("edit.loading")} back={student ? `/student/${student.id}/profile` : "/staff"} />;
  }

  const history = submissionsForStudent(student.id)
    .map((row) => ({
      row,
      form: forms.find((form) => form.id === row.formId),
    }))
    .filter((item) => item.form);
  const current = history.find((item) => item.form?.status === "open") ?? history[0];
  const openForm = forms.find((form) => form.status === "open") ?? current?.form;
  const extraQuestions = openForm
    ? formCopyForLocale(openForm.copy, locale).extraQuestions
    : [];

  return (
    <StudentEditor
      mode="edit"
      studentId={student.id}
      seed={permissionForEdit(student, current?.row.answers.permission)}
      extraSeed={current?.row.answers.extraAnswers ?? {}}
      formId={openForm?.id ?? ""}
      formName={openForm?.name ?? ""}
      extraQuestions={extraQuestions}
      staff={account.role === "staff"}
    />
  );
}

export function StaffCreateStudent() {
  const { account, loading } = useAuth();
  const { forms } = useForms();
  const { locale, t } = useLocale();
  const signInTo = "/signin?role=staff&next=%2Fstaff%2Fstudents%2Fnew";

  if (loading) return <Status note={t("edit.loading")} />;
  if (!account) return <Navigate to={signInTo} replace />;
  if (account.role !== "staff") return <Navigate to="/" replace />;

  const formsReady = !remoteClient() || forms.length > 0;
  if (!formsReady) return <Status note={t("edit.loading")} back="/staff" />;

  const openForm = forms.find((form) => form.status === "open");
  const extraQuestions = openForm
    ? formCopyForLocale(openForm.copy, locale).extraQuestions
    : [];

  return (
    <StudentEditor
      mode="create"
      studentId=""
      seed={emptyPermission()}
      extraSeed={{}}
      formId={openForm?.id ?? ""}
      formName={openForm?.name ?? ""}
      extraQuestions={extraQuestions}
      staff
    />
  );
}

function Status({ note, back }: { note: string; back?: string }) {
  const { t } = useLocale();
  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <p className="anno">{note}</p>
        {back ? <Link to={back}>{t("edit.back")}</Link> : null}
      </main>
    </div>
  );
}

function StudentEditor({
  mode,
  studentId,
  seed,
  extraSeed,
  formId,
  formName,
  extraQuestions,
  staff,
}: {
  mode: "edit" | "create";
  studentId: string;
  seed: PermissionAnswers;
  extraSeed: Record<string, string>;
  formId: string;
  formName: string;
  extraQuestions: FormQuestion[];
  staff: boolean;
}) {
  const navigate = useNavigate();
  const { addStudent, updateStudent } = useStudents();
  const { replaceAnswers } = useForms();
  const { locale, t } = useLocale();
  const chrome = registrationChrome(locale);
  const creating = mode === "create";
  const sampleTools = creating && sampleToolsEnabled(runtimeFromBrowser().name);
  const [permission, setPermission] = useState(seed);
  const [extraAnswers, setExtraAnswers] = useState(extraSeed);
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState("");
  const [errorFieldId, setErrorFieldId] = useState("");
  const [busy, setBusy] = useState(false);
  const step = REGISTRATION_STEPS[stepIndex] ?? "student";
  const name = `${seed.student.firstName} ${seed.student.lastName}`.trim();
  const formComplete = permissionIssue(permission) === null;
  const backTo = creating ? "/staff" : `/student/${studentId}/profile`;

  function showIssue(fieldId: string, message: string, nextStep = stepIndex) {
    setError(message);
    setErrorFieldId(fieldId);
    if (nextStep !== stepIndex) setStepIndex(nextStep);
  }

  function issueOnOrBefore(index: number) {
    const issue = permissionIssue(permission);
    if (!issue) return null;
    const issueIndex = REGISTRATION_STEPS.indexOf(registrationStepForField(issue.fieldId));
    if (issueIndex > index) return null;
    return issue;
  }

  function onContinue() {
    const issue = issueOnOrBefore(stepIndex);
    if (issue) {
      const issueIndex = REGISTRATION_STEPS.indexOf(registrationStepForField(issue.fieldId));
      showIssue(issue.fieldId, locale === "es" ? issue.es : issue.en, issueIndex);
      return;
    }
    setError("");
    setErrorFieldId("");
    setStepIndex((current) => Math.min(current + 1, REGISTRATION_STEPS.length - 1));
  }

  async function onSave() {
    const issue = permissionIssue(permission);
    if (issue) {
      const issueIndex = REGISTRATION_STEPS.indexOf(registrationStepForField(issue.fieldId));
      showIssue(issue.fieldId, locale === "es" ? issue.es : issue.en, issueIndex);
      return;
    }
    const missingExtra = missingRequiredQuestion(extraQuestions, extraAnswers);
    if (missingExtra) {
      setError(t("register.errorQuestion", { label: missingExtra.label }));
      setErrorFieldId(missingExtra.id);
      setStepIndex(REGISTRATION_STEPS.indexOf("review"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const core = permissionCore(permission);
      const saved = creating
        ? await addStudent(core)
        : await updateStudent(studentId, core);
      if (formId) {
        await replaceAnswers(
          formId,
          saved.id,
          answersFromStudent(saved, undefined, extraAnswers, permission),
        );
      }
      navigate(`/student/${saved.id}/profile`);
    } catch (saveError) {
      setBusy(false);
      setError(saveError instanceof Error ? saveError.message : t(creating ? "create.error" : "edit.error"));
    }
  }

  function onFillSample() {
    const next = randomPermissionAnswers();
    setPermission(next);
    setExtraAnswers(
      Object.fromEntries(
        extraQuestions.map((question) => [question.id, randomAnswerForQuestion(question)]),
      ),
    );
    setError("");
    setErrorFieldId("");
    setStepIndex(REGISTRATION_STEPS.length - 1);
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell reg-page">
        {staff ? <StaffNav /> : <PublicNav />}
        <div className="reg-wrap">
          <p className="meta">{t(creating ? "create.meta" : "edit.meta")}</p>
          <h1>{creating ? t("create.title") : t("edit.title", { name })}</h1>
          <p className="lede">{t(creating ? "create.lede" : "edit.lede")}</p>
          {sampleTools ? (
            <div className="qa-sample">
              <p className="qa-sample-tag">{t("sample.testOnly")}</p>
              <p>{t("create.sample")}</p>
              <button type="button" className="btn ghost" onClick={onFillSample} disabled={busy}>
                {t("family.sample")}
              </button>
            </div>
          ) : null}
          {formName ? (
            <p className="reg-year">
              <span>{t("register.currentForm")}</span>
              <strong>{formName}</strong>
            </p>
          ) : (
            <div className="flash flash--warning" role="status">
              {t("edit.noForm")}
            </div>
          )}

          <form className="reg-form" onSubmit={(event) => event.preventDefault()} noValidate>
            <nav className="reg-progress" aria-label={chrome.progress}>
              <ol>
                {REGISTRATION_STEPS.map((id, index) => (
                  <li key={id}>
                    <button
                      type="button"
                      aria-current={id === step ? "step" : undefined}
                      disabled={busy || (!formComplete && index > stepIndex)}
                      data-complete={index < stepIndex ? "true" : "false"}
                      onClick={() => {
                        setError("");
                        setErrorFieldId("");
                        setStepIndex(index);
                      }}
                    >
                      <span className="reg-progress__num">{index + 1}</span>
                      <span className="reg-progress__label">{chrome.short[id]}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </nav>

            <PermissionFormFields
              value={permission}
              onChange={(next) => {
                setPermission(next);
                if (errorFieldId) {
                  setError("");
                  setErrorFieldId("");
                }
              }}
              step={step}
              errorFieldId={errorFieldId}
              errorMessage={error}
              onEditStep={(next) => {
                setError("");
                setErrorFieldId("");
                setStepIndex(REGISTRATION_STEPS.indexOf(next));
              }}
            />

            {step === "review" && extraQuestions.length > 0 ? (
              <section className="reg-extra" aria-labelledby="reg-extra-title">
                <h2 id="reg-extra-title" className="reg-subhead">
                  {t("register.moreQuestions")}
                </h2>
                {extraQuestions.map((item) => (
                  <div
                    className={`field${errorFieldId === item.id ? " field--invalid" : ""}`}
                    id={`${item.id}-field`}
                    key={item.id}
                  >
                    <FormFieldLabel
                      htmlFor={item.id}
                      required={item.required}
                      optional={!item.required}
                      presentation="asterisk"
                    >
                      {item.label}
                    </FormFieldLabel>
                    <QuestionFill
                      question={item}
                      value={extraAnswers[item.id] ?? ""}
                      onChange={(next) =>
                        setExtraAnswers((current) => ({ ...current, [item.id]: next }))
                      }
                    />
                  </div>
                ))}
              </section>
            ) : null}

            {error ? <ErrorFlash key={error} message={error} /> : null}

            <div className="reg-nav">
              {stepIndex > 0 ? (
                <button
                  type="button"
                  className="reg-back"
                  onClick={() => {
                    setError("");
                    setErrorFieldId("");
                    setStepIndex((current) => Math.max(current - 1, 0));
                  }}
                  disabled={busy}
                >
                  {chrome.back}
                </button>
              ) : (
                <Link className="reg-back" to={backTo}>
                  {t(creating ? "create.back" : "edit.back")}
                </Link>
              )}
              {step === "review" ? (
                <button type="button" className="reg-submit" onClick={() => void onSave()} disabled={busy}>
                  {busy
                    ? t(creating ? "create.saving" : "edit.saving")
                    : t(creating ? "create.save" : "edit.save")}
                </button>
              ) : (
                <button type="button" className="reg-next" onClick={onContinue} disabled={busy}>
                  {chrome.continue}
                </button>
              )}
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
